/**
 * ONE LINK PER GAME (job #742, Tom 2026-10-04): every game has its own address that opens straight into
 * that one game, no chooser, and More games goes to the hub. The two games (job #738) each have a
 * names version and a map version, and the hub is the family's bare address, /guess:
 *
 *   /guess   /guess/mystery-chat   /guess/mystery-chat/map   /guess/i-love-you   /guess/i-love-you/map
 *
 * Tom asked for the hub at /games, but /games is Games night's address (job #920, router), so the hub
 * takes /guess, which he also asked to keep working.
 *
 * A share link is the game's own address with the score in the query (`/guess/i-love-you?s=8&l=cym`), so
 * it opens into the game it came from. The old addresses keep working: /guess is the hub, /guess/map goes to
 * it, and an old share link (`/guess?g=love&s=7`) goes to that game's own address with its score.
 *
 * A leaf module, read by the router, so it imports nothing.
 */
export type LinkedGame = 'mystery' | 'love'

/** The short challenge link's prefix, /g/<code> (job #951). Here, not in challenge.ts, so the router can name the route without loading the game. */
export const SHORT_PATH = '/g'

/** Each game's slug, in the hub's order. */
export const GAME_SLUGS: Record<LinkedGame, string> = { mystery: 'mystery-chat', love: 'i-love-you' }
export const LINKED_GAMES = Object.keys(GAME_SLUGS) as LinkedGame[]

/** The game a slug names, or null. */
export function gameFromSlug(slug: unknown): LinkedGame | null {
  return LINKED_GAMES.find((k) => GAME_SLUGS[k] === slug) ?? null
}

/** A game's own address, its map version when `map`. */
export function gamePath(game: LinkedGame, map = false): string {
  return `/guess/${GAME_SLUGS[game]}${map ? '/map' : ''}`
}

/** The hub's address. */
export const HUB_PATH = '/guess'

/**
 * PIN IT (job #798): the globe game, one address of its own with no names or map version, since a globe is
 * what it is. Its share link is the same address with the raw score, `/guess/pin-it?s=640`.
 */
export const PIN_SLUG = 'pin-it'
export const PIN_PATH = `/guess/${PIN_SLUG}`

/** LISTEN (job #811): tap a country on the globe, hear I love you from there. Free play, its own address, no share score. */
export const LISTEN_SLUG = 'listen'
export const LISTEN_PATH = `/guess/${LISTEN_SLUG}`

/**
 * Where an old share link on /guess or /guess/map goes: one naming either game opens that game's own
 * address with the score and language it carried; anything else is no game, null, and stays on the hub.
 */
export function legacyGuessTarget(query: Record<string, unknown>, map: boolean): { path: string; query: Record<string, string> } | null {
  const g = typeof query.g === 'string' && (LINKED_GAMES as string[]).includes(query.g) ? (query.g as LinkedGame) : null
  if (!g) return null
  const rest: Record<string, string> = {}
  for (const k of ['s', 'l']) if (typeof query[k] === 'string') rest[k] = query[k] as string
  return { path: gamePath(g, map), query: rest }
}

/**
 * The question each map card on the hub pictures: a real four-area question of the kind the game
 * asks, its choices in one region (globeFrame.framable), the love card's from languages that say I love you
 * in the game. Pinned so the hub reads the same on every visit; GamesHubView.test.ts proves each is a
 * question the game could ask.
 */
export const THUMB_QUESTIONS: Record<LinkedGame, string[]> = {
  mystery: ['fra', 'deu', 'pol', 'ita'],
  love: ['spa', 'ita', 'ell', 'tur'],
}
